import type { SearchContextType, SearchResult } from '@gat-ship/types'
import type { SearchContextMetadata } from './global-search-context-metadata'

export interface SearchContextItem {
  context: SearchContextType
  metadata: SearchContextMetadata
}

export interface ResultGroup {
  context: SearchContextType
  title: string
  total: number
  visible: SearchResult[]
  hasMore: boolean
}

/**
 * The context tiles offered before any search runs: every context, but
 * `global` is dropped while the input is empty (you can't fan out across
 * all contexts with no query). Order follows the metadata registry.
 */
export function selectContextItems(
  contextMetadata: Record<SearchContextType, SearchContextMetadata>,
  isSearchInputEmpty: boolean
): SearchContextItem[] {
  return (
    Object.entries(contextMetadata) as [
      SearchContextType,
      SearchContextMetadata
    ][]
  )
    .filter(([context]) => !(context === 'global' && isSearchInputEmpty))
    .map(([context, metadata]) => ({ context, metadata }))
}

/**
 * Groups results by context for the default (multi-context) view, slicing
 * each group to `maxPerContext` and flagging `hasMore`. Group order follows
 * each context's first appearance in `results`. Returns `[]` when there are
 * no results or a single-context tag is active (the flat view renders then).
 */
export function groupResultsByContext(
  results: readonly SearchResult[] | undefined,
  isTagActive: boolean,
  contextMetadata: Record<SearchContextType, SearchContextMetadata>,
  maxPerContext: number
): ResultGroup[] {
  if (!results || isTagActive) {
    return []
  }
  const byContext = new Map<SearchContextType, SearchResult[]>()
  for (const result of results) {
    const group = byContext.get(result.contextType) ?? []
    group.push(result)
    byContext.set(result.contextType, group)
  }
  return [...byContext.entries()].map(([context, items]) => ({
    context,
    title: contextMetadata[context].title,
    total: items.length,
    visible: items.slice(0, maxPerContext),
    hasMore: items.length > maxPerContext
  }))
}

/**
 * The flat result list for the tag-active (single-context) view; empty
 * when no tag is active (the grouped view renders instead).
 */
export function flattenResultsForTag(
  results: readonly SearchResult[] | undefined,
  isTagActive: boolean
): SearchResult[] {
  return isTagActive ? [...(results ?? [])] : []
}
